/** @jsxImportSource @emotion/react */

import { vars } from "@imwebme/clay-token";
import type { Ref } from "react";
import { forwardRef, useMemo, useState } from "react";
import { Flex, Label, Typography } from "..";
import { TextareaCore } from "./Textarea.core";
import { maxLengthCSS, textareaWrapperCSS } from "./Textarea.css";
import type { TextareaProps } from "./Textarea.types";
import { CharacterCount } from "./components";

export const Textarea = forwardRef(
  (
    {
      label,
      labelProps,
      helperText,
      errorText,
      className,
      maxLength,
      isError,
      sx,
      height,
      value,
      isFocus,
      characterCount,
      ...restCoreProps
    }: TextareaProps,
    ref: Ref<HTMLTextAreaElement>,
  ) => {
    const [_isFocus, setIsFocus] = useState(isFocus);

    const computedHelperText = useMemo(() => {
      if (errorText || helperText) {
        if (isError) return errorText;

        return helperText;
      }

      return null;
    }, [errorText, helperText, isError]);

    const minHeight = useMemo(() => {
      switch (height) {
        case "min":
          return "100px";

        case "max":
          return "184px";

        default:
          return "140px";
      }
    }, [height]);

    return (
      <Flex.Column gap={vars.spacing[15]} className={className} sx={sx}>
        {label && <Label text={label} {...labelProps} />}
        <div css={textareaWrapperCSS({ isError, isFocus: _isFocus, minHeight, characterCount })}>
          <TextareaCore
            ref={ref}
            setIsFocus={setIsFocus}
            value={value}
            maxLength={maxLength}
            {...restCoreProps}
          />

          {characterCount && (
            <CharacterCount sx={{ ...maxLengthCSS }} value={value} maxLength={maxLength} />
          )}
        </div>

        {computedHelperText && (
          <Typography variant="body-small" colorToken={isError ? "textCritical" : "textSub"}>
            {computedHelperText}
          </Typography>
        )}
      </Flex.Column>
    );
  },
);

Textarea.displayName = "Textarea";
